import React, { useState, useMemo } from 'react';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import PriceInputUI from '../../../input/PriceInputUI';
import { CheckIcon, DocumentCheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import ReturnItemInfo from '../ReturnItemInfo';

// RTK Query hooks for group-level approve/reject operations
import {
  useApproveAllReturnsMutation,
  useRejectAllReturnsMutation
} from '../../../../redux/apis/endpoints/fulfillmentGroupApi';
import {
  useGetReturnRequestByOrderItemQuery
} from '../../../../redux/apis/endpoints/requestApi';

/**
 * ProcessReturnRequestsModal
 * Allows a merchant to approve or reject all return requests within a fulfillment group.
 *
 * Props expected from ActionModalRenderer through useActionHandler:
 *  - isOpen: boolean – modal visibility
 *  - onClose: () => void – called when user closes the modal (cancels)
 *  - onConfirm: (result) => void – called when all mutations finish successfully
 *  - entity: { orderItems: [...], id, ... } – fulfillment group entity containing order items
 *  - isLoading: boolean – optional external loading flag (unused here)
 */
const ProcessReturnRequestsModal = ({
  isOpen,
  onClose,
  onConfirm,
  entity,
  isLoading = false
}) => {
  // Extract only items that currently have a return request pending
  const pendingReturnItems = useMemo(() => {
    if (!entity?.orderItems) return [];
    return entity.orderItems.filter(
      item => item.status === 'return_requested_by_customer'
    );
  }, [entity]);

  // Since FG return reason is same for all items, extract once
  const groupReturnReason = useMemo(() => {
    if (pendingReturnItems.length === 0) return '—';
    const rr = pendingReturnItems[0]?.returnRequest;
    if (!rr) return '—';
    const extract = details => {
      if (!details) return undefined;
      if (typeof details === 'string') {
        try { return JSON.parse(details).reason; } catch { return undefined; }
      }
      return details.reason;
    };
    const raw = rr.reason || extract(rr.requestDetails) || '—';
    return raw.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
  }, [pendingReturnItems]);

  // Single decision for all items: 'approve' | 'reject'
  const [decision, setDecision] = useState('approve');
  const [rejectionReason, setRejectionReason] = useState(''); // Single reason for all items
  const [usageFee, setUsageFee] = useState('');

  const currency = pendingReturnItems[0]?.currency || 'USD';

  // Group-level mutations
  const [approveAllReturns, { isLoading: isApproving }] = useApproveAllReturnsMutation();
  const [rejectAllReturns, { isLoading: isRejecting }] = useRejectAllReturnsMutation();

  const anyLoading = isLoading || isApproving || isRejecting;

  const executeDecision = async () => {
    if (pendingReturnItems.length === 0) return;

    try {
      console.log(`🔄 Processing ${decision} for all return requests in fulfillment group ${entity.id}`);
      
      if (decision === 'approve') {
        const payload = {
          fulfillmentGroupId: entity.id,
          approvalComments: `Usage fee: ${usageFee || '0'}`,
          usageFee: parseFloat(usageFee) || 0
        };
        
        console.log('[ProcessReturnRequestsModal] Calling approveAllReturns with payload:', payload);
        
        // Set loading state manually before making the API call
        let apiError = null;
        let apiResult = null;

        try {
          // Use unwrap() to properly handle the Promise and throw on error
          apiResult = await approveAllReturns(payload).unwrap();
          console.log('[ProcessReturnRequestsModal] Success response:', apiResult);
          
          // Only show success toast if we got a successful response
          toast.success(
            `All ${pendingReturnItems.length} return request${pendingReturnItems.length !== 1 ? 's' : ''} approved.`
          );
          
          // Close modal and call onConfirm only on success
          onConfirm({ 
            approvedIds: pendingReturnItems.map(item => item.id), 
            rejectedIds: [] 
          });
          closeAndReset();
        } catch (error) {
          apiError = error;
          console.error('[ProcessReturnRequestsModal] Error in approveAllReturns:', error);
          // Show a specific error for the API call
          toast.error(`Error approving returns: ${error.message || 'Unknown error'}`);
        }
      } else if (decision === 'reject') {
        const payload = {
          fulfillmentGroupId: entity.id,
          rejectionReason: rejectionReason || 'All returns rejected via bulk action'
        };
        
        console.log('[ProcessReturnRequestsModal] Calling rejectAllReturns with payload:', payload);
        
        // Set loading state manually before making the API call
        let apiError = null;
        let apiResult = null;

        try {
          // Use unwrap() to properly handle the Promise and throw on error
          apiResult = await rejectAllReturns(payload).unwrap();
          console.log('[ProcessReturnRequestsModal] Success response:', apiResult);
          
          // Only show success toast if we got a successful response
          toast.success(
            `All ${pendingReturnItems.length} return request${pendingReturnItems.length !== 1 ? 's' : ''} rejected.`
          );
          
          // Close modal and call onConfirm only on success
          onConfirm({ 
            approvedIds: [], 
            rejectedIds: pendingReturnItems.map(item => item.id) 
          });
          closeAndReset();
        } catch (error) {
          apiError = error;
          console.error('[ProcessReturnRequestsModal] Error in rejectAllReturns:', error);
          // Show a specific error for the API call
          toast.error(`Error rejecting returns: ${error.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      // This is for any unexpected errors in the UI logic itself, not API errors
      console.error('[ProcessReturnRequestsModal] Unexpected UI error:', error);
      toast.error('Unexpected error in the UI. Please try again.');
    }
  };

  const closeAndReset = () => {
    setDecision('approve');
    setRejectionReason('');
    setUsageFee('');
    onClose();
  };

  // Child row component to keep hooks compliant
  const ReturnItemRow = ({ item }) => {
    // Only fetch return request data if we have a valid item ID
    // and don't already have the return request data
    const shouldSkipQuery = !item?.id || 
                          !Number.isFinite(Number(item.id)) ||
                          !!item.returnRequest?.reason || 
                          !!item.returnRequest?.requestDetails?.reason;
    
    const { data: reqData } = useGetReturnRequestByOrderItemQuery(
      // Make sure we pass a valid ID, not undefined or any non-numeric value
      Number.isFinite(Number(item.id)) ? item.id : undefined, 
      {
        skip: shouldSkipQuery,
        // Don't throw or show errors for this query - it's just supplementary data
        refetchOnMountOrArgChange: false
      }
    );

    // Helper to safely extract reason from possible JSON string
    const extractReason = details => {
      if (!details) return undefined;
      if (typeof details === 'string') {
        try {
          const parsed = JSON.parse(details);
          return parsed.reason;
        } catch (err) {
          return undefined;
        }
      }
      return details.reason;
    };

    // Safely extract reason, falling back through multiple sources
    const reason =
      item?.returnRequest?.reason ||
      extractReason(item?.returnRequest?.requestDetails) ||
      reqData?.reason ||
      extractReason(reqData?.requestDetails) ||
      '—';

    // Calculate how many days the customer has had the product
    const deliveryTimestamp =
      item?.deliveredAt || // prefer item-level deliveredAt if present
      item?.delivered_at ||
      entity?.deliveredAt ||
      entity?.delivered_at ||
      entity?.fulfillmentGroup?.deliveredAt ||
      entity?.fulfillmentGroup?.delivered_at;

    let daysHeldDisplay = 'N/A';
    if (deliveryTimestamp) {
      const deliveredDate = new Date(deliveryTimestamp);
      if (!isNaN(deliveredDate.getTime())) {
        const diffMs = Date.now() - deliveredDate.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours < 24) {
          const hrs = Math.floor(diffHours);
          daysHeldDisplay = `${hrs} hour${hrs !== 1 ? 's' : ''}`;
        } else {
          const diffDays = Math.floor(diffHours / 24);
          daysHeldDisplay = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
        }
      }
    }

    return (
      <div className="border border-gray-200 rounded-lg p-4" key={item.id}>
        <ReturnItemInfo item={item} />
      </div>
    );
  };

  // Move customer-held summary above Decision Controls
  // Summary of how long customer has held the FG (based on FG deliveredAt or first item)
  const fgDeliveryTimestamp =
    entity?.deliveredAt ||
    entity?.delivered_at ||
    entity?.fulfillmentGroup?.deliveredAt ||
    entity?.fulfillmentGroup?.delivered_at ||
    (pendingReturnItems[0]?.deliveredAt || pendingReturnItems[0]?.delivered_at);

  let fgHeldDisplay = null;
  if (fgDeliveryTimestamp) {
    const deliveredDate = new Date(fgDeliveryTimestamp);
    if (!isNaN(deliveredDate.getTime())) {
      const diffMs = Date.now() - deliveredDate.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      if (diffHours < 24) {
        const hrs = Math.floor(diffHours);
        fgHeldDisplay = `${hrs} hour${hrs !== 1 ? 's' : ''}`;
      } else {
        const diffDays = Math.floor(diffHours / 24);
        fgHeldDisplay = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={closeAndReset} title="Process Return Requests" size="lg">
      <div className="py-4">
        <div className="flex items-center mb-4">
          <DocumentCheckIcon className="h-12 w-12 text-blue-500 mr-4" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Process Return Requests
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Approve or reject the return request for this fulfillment group.
            </p>
          </div>
        </div>

        {pendingReturnItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No pending return requests in this group.</p>
          </div>
        ) : (
          <div className="space-y-4">
             {/* Group Return Reason & Held Duration */}
             <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 space-y-1">
              <p className="text-sm"><span className="font-medium">Return Reason:</span> {groupReturnReason}</p>
              {fgHeldDisplay && (
                <p className="text-sm"><span className="font-medium">Customer held:</span> {fgHeldDisplay}</p>
              )}
            </div>
            
            {/* Decision Controls */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Decision for all {pendingReturnItems.length} item{pendingReturnItems.length !== 1 ? 's' : ''}:
              </h4>
              
              <div className="flex items-center gap-6 mb-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="decision"
                    value="approve"
                    checked={decision === 'approve'}
                    onChange={(e) => setDecision(e.target.value)}
                    className="h-4 w-4 text-brand-teal focus:ring-brand-teal border-gray-300"
                  />
                  <span className="text-sm font-medium text-green-700">Approve Return</span>
                </label>

                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="decision"
                    value="reject"
                    checked={decision === 'reject'}
                    onChange={(e) => setDecision(e.target.value)}
                    className="h-4 w-4 text-brand-teal focus:ring-brand-teal border-gray-300"
                  />
                  <span className="text-sm font-medium text-red-700">Reject Return</span>
                </label>
              </div>

              {decision === 'reject' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection reason (optional)
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={3}
                    placeholder="Provide rejection reason for all items (optional)"
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                  />
                </div>
              )}

              {decision === 'approve' && (
                <div className="mt-4 space-y-3">
                
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Usage fee to charge customer</label>
                    <PriceInputUI
                      name="usageFee"
                      value={usageFee}
                      onChange={e=>setUsageFee(e.target.value)}
                      onCurrencyChange={()=>{}}
                      currency={currency}
                      required={false}
                      disableCurrencyChange={true}
                      compact={true}
                    />
                  </div>
                </div>
              )}
            </div>

           

            {/* Items List */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Return Item(s):
              </h4>
              <div className="max-h-[50vh] overflow-y-auto space-y-3">
                {pendingReturnItems.map(item => <ReturnItemRow key={item.id} item={item} />)}
              </div>
            </div>
          </div>
        )}

        

        {/* Footer Buttons */}
        <div className="flex flex-col sm:flex-row-reverse gap-3 pt-4 border-t border-gray-100 mt-4">
          <Button
            variant="primary"
            onClick={executeDecision}
            disabled={anyLoading || pendingReturnItems.length === 0}
            isLoading={anyLoading}
            className="flex items-center justify-center"
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            {decision === 'approve' ? 'Approve' : 'Reject'} All Returns ({pendingReturnItems.length} item{pendingReturnItems.length !== 1 ? 's' : ''})
          </Button>
          <Button variant="secondary" onClick={closeAndReset} disabled={anyLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ProcessReturnRequestsModal; 